import { useEffect, useState, type FormEvent } from 'react';
import type { Activity, NewActivity } from '@personical/cc-shared-types';
import type { ApiClient } from '../api.js';

const TYPE_LABEL: Record<Activity['type'], string> = {
  call_in: 'Incoming call',
  call_out: 'Outgoing call',
  call_missed: 'Missed call',
  note: 'Note',
  audit: 'Audit'
};

const TYPE_ICON: Record<Activity['type'], string> = {
  call_in: '↙',
  call_out: '↗',
  call_missed: '✕',
  note: '✎',
  audit: 'ⓘ'
};

const TYPE_COLOR: Record<Activity['type'], string> = {
  call_in: 'text-emerald-700 bg-emerald-50 dark:text-emerald-300 dark:bg-emerald-900/30',
  call_out: 'text-sky-700 bg-sky-50 dark:text-sky-300 dark:bg-sky-900/30',
  call_missed: 'text-red-700 bg-red-50 dark:text-red-300 dark:bg-red-900/30',
  note: 'text-mid surface-muted',
  audit: 'text-mid surface-muted'
};

interface Props {
  api: ApiClient;
  contactId: string;
  defaultPhoneNumber?: string;
  currentOid: string | null;
}

export function ActivityTimeline({ api, contactId, defaultPhoneNumber, currentOid }: Props) {
  const [items, setItems] = useState<Activity[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [type, setType] = useState<Activity['type']>('note');
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .listActivities(contactId)
      .then((rows) => {
        if (!cancelled) setItems(rows);
      })
      .catch((err: Error) => {
        if (!cancelled) setError(err.message);
      });
    return () => {
      cancelled = true;
    };
  }, [api, contactId]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const input: NewActivity = {
        contactId,
        type,
        timestamp: new Date().toISOString(),
        userId: currentOid ?? undefined,
        notes: text.trim() || undefined,
        phoneNumber: type !== 'note' ? defaultPhoneNumber : undefined,
        source: 'manual'
      };
      const created = await api.createActivity(contactId, input);
      setItems((prev) => [created, ...(prev ?? [])]);
      setText('');
      setType('note');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  async function remove(id: string) {
    try {
      await api.deleteActivity(id);
      setItems((prev) => prev?.filter((a) => a.id !== id) ?? null);
    } catch (err) {
      setError((err as Error).message);
    }
  }

  return (
    <section className="space-y-4">
      <header>
        <h3 className="text-xs uppercase tracking-wider text-muted mb-1">Activity</h3>
      </header>

      <form onSubmit={submit} className="flex gap-2 flex-wrap items-stretch">
        <select
          value={type}
          onChange={(e) => setType(e.target.value as Activity['type'])}
          className="rounded-md border border-input bg-white px-2 py-2 text-sm"
        >
          <option value="note">Note</option>
          <option value="call_in">Incoming call</option>
          <option value="call_out">Outgoing call</option>
          <option value="call_missed">Missed call</option>
        </select>
        <input
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={
            type === 'note'
              ? 'What happened?'
              : 'Optional note (outcome, summary)…'
          }
          className="flex-1 min-w-[180px] rounded-md border border-input bg-white px-3 py-2 text-sm"
        />
        <button
          type="submit"
          disabled={submitting || (type === 'note' && !text.trim())}
          className="btn-primary px-3 py-2 text-sm font-medium disabled:opacity-50"
        >
          {submitting ? 'Saving…' : 'Log'}
        </button>
      </form>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-900 text-red-800 dark:text-red-300 text-sm rounded p-3">{error}</div>
      )}

      {items === null ? (
        <p className="text-sm text-muted">Loading…</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-muted">No activity yet.</p>
      ) : (
        <ul className="space-y-2">
          {items.map((a) => (
            <li
              key={a.id}
              className="flex gap-3 items-start rounded-lg border border-divider surface-soft p-3"
            >
              <span
                className={`shrink-0 inline-flex items-center justify-center rounded-full text-sm h-7 w-7 ${TYPE_COLOR[a.type]}`}
                aria-hidden="true"
              >
                {TYPE_ICON[a.type]}
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm">
                  <span className="font-medium">{TYPE_LABEL[a.type]}</span>
                  {a.outcome && <span className="text-muted"> · {a.outcome}</span>}
                  {a.durationSeconds !== undefined && (
                    <span className="text-muted"> · {formatDuration(a.durationSeconds)}</span>
                  )}
                </p>
                {a.notes && <p className="text-sm text-mid whitespace-pre-wrap">{a.notes}</p>}
                <p className="text-xs text-faint mt-0.5">
                  {new Date(a.timestamp).toLocaleString()}
                  {a.source && a.source !== 'manual' && ` · via ${a.source}`}
                </p>
              </div>
              <button
                type="button"
                onClick={() => remove(a.id)}
                className="text-xs text-faint hover:text-red-700"
                aria-label="Delete activity"
                title="Delete activity"
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;
  return remainder > 0 ? `${minutes}m ${remainder}s` : `${minutes}m`;
}
